/**
 * ImportedDocumentAssess — route screen for department users to assess (comment
 * on) imported-document fragments assigned to their department.
 *
 * Mounted at /imported-documents/assess/:id. Deliberately a lean shell — it does
 * NOT reuse the heavy ImportedDocumentForm (no tabs / AI / print / delete) to
 * keep the edit and assess flows decoupled. Comments are discussion messages
 * posted immediately (no global save step), so there's no unsaved-changes guard.
 */

import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Button, Container, Grid, Stack, AppBar, IconButton, Tooltip } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Localized } from "@fluent/react";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { ErrorContainer } from "../../../app/ErrorContainer";
import { LoadingContainer } from "../../../app/LoadingContainer";
import "./importedDocument.css";
import { loadImportedDocumentById } from "./importedDocumentsApi";
import { selectAllElements, selectImportedDocument } from "./importedDocSlice";
import { ImportedDocumentHeader } from "./components/ImportedDocumentHeader";
import { loadAllDepartmentAssignements } from "./importedDocDepartmentsSlice";
import { loadAllHiddenParts } from "./importedDocHiddenPartsSlice";
import AssessDocumentReview from "./ast/AssessDocumentReview";
import { AssessmentHelpDialog } from "./components/AssessmentHelpDialog";

// Marks that the user has seen the assessment coach mark (once per browser).
const HELP_SEEN_KEY = "assess-help-seen";
const helpSeen = () => {
    try {
        return localStorage.getItem(HELP_SEEN_KEY) === "1";
    } catch {
        return true;
    }
};

export const ImportedDocumentAssess = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { id: paramId } = useParams();
    const id: number = parseInt(paramId ?? "0");

    const { doc } = useAppSelector(selectImportedDocument);
    const elements = useAppSelector(selectAllElements);

    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [error, setError] = useState<any>(null);
    const [helpOpen, setHelpOpen] = useState(() => !helpSeen());

    const closeHelp = (dontShowAgain: boolean) => {
        if (dontShowAgain) {
            try {
                localStorage.setItem(HELP_SEEN_KEY, "1");
            } catch {
                /* ignore storage failures */
            }
        }
        setHelpOpen(false);
    };

    const load = useCallback(async () => {
        if (!id) return;
        setIsLoading(true);
        setIsError(false);
        setError(null);
        try {
            await dispatch(loadImportedDocumentById(id)).unwrap();
            await dispatch(loadAllDepartmentAssignements({ docId: id }));
            // Assessors see the reviewer's column choices applied, so the
            // document reads the same on both screens.
            await dispatch(loadAllHiddenParts({ docId: id }));
        } catch (err) {
            console.error(err);
            setIsError(true);
            setError(err);
        } finally {
            setIsLoading(false);
        }
    }, [dispatch, id]);

    useEffect(() => {
        load();
    }, [load]);

    if (!id) {
        return <ErrorContainer error={{ message: "Brak parametru id w URL." }} />;
    }
    if (isError) {
        return <ErrorContainer error={error} />;
    }
    if (isLoading || !doc) {
        return <LoadingContainer />;
    }

    return (
        <Container
            maxWidth={false}
            sx={{ padding: "2px", paddingLeft: "2px!important", paddingRight: "2px!important" }}
        >
            <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <ImportedDocumentHeader document={doc} />
                </Box>
                <Tooltip title={<Localized id="assess-help-button"><span>_Help</span></Localized>}>
                    <IconButton onClick={() => setHelpOpen(true)} size="small" sx={{ mt: 0.5 }}>
                        <HelpOutlineIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <AssessmentHelpDialog open={helpOpen} onClose={closeHelp} />

            <Grid container>
                <Grid item xs={12} sx={{ pt: 1 }}>
                    <AssessDocumentReview docId={doc.id} docGuid={doc.guid} elements={elements} />
                </Grid>
            </Grid>

            <AppBar position="fixed" color="primary" sx={{ top: "auto", bottom: 0, p: 0.75 }}>
                <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ px: 1 }}>
                    <Box />
                    <Button onClick={() => navigate(-1)} variant="contained" size="small">
                        <Localized id="back">Wróć</Localized>
                    </Button>
                </Stack>
            </AppBar>
        </Container>
    );
};

export default ImportedDocumentAssess;
